import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Container } from 'reactstrap';
import { ChevronDown } from 'lucide-react';
import { resolveIsCryptoLoan } from '../Dashboard/utils/cryptoUtils';
import {
  CRYPTO_FAQ_CATEGORIES,
  FAQ_LOAN_TYPES,
  REGULAR_FAQ_CATEGORIES,
  getQuestionKey,
} from './faqData';
import './styles.scss';

const FAQs = ({ loansList }) => {
  const isCryptoLoan = resolveIsCryptoLoan(loansList);
  const [selectedLoanType, setSelectedLoanType] = useState(
    isCryptoLoan ? FAQ_LOAN_TYPES.CRYPTO : FAQ_LOAN_TYPES.REGULAR
  );
  const [openQuestionKey, setOpenQuestionKey] = useState(null);

  const handleLoanTypeChange = (type) => {
    setSelectedLoanType(type);
    setOpenQuestionKey(null);
  };

  const categories = selectedLoanType === FAQ_LOAN_TYPES.CRYPTO
    ? CRYPTO_FAQ_CATEGORIES
    : REGULAR_FAQ_CATEGORIES;

  const toggleQuestion = (key) => {
    setOpenQuestionKey((current) => (current === key ? null : key));
  };

  return (
    <div className="page-faqs">
      <Container fluid className="faq-container">
        <h1 className="faq-page-title">Frequently Asked Questions</h1>

        {/* Info Callout */}
        <div className="faq-info-callout">
          <p className="faq-info-callout__title">Select your loan type below</p>
          <p className="faq-info-callout__text">
            Choose your loan type to read the FAQs most relevant to you. If you have a
            crypto-secured loan, select Crypto Secured Loan. General questions about
            payments, payoff, and account management also apply to your loan and are available
            under Unsecured Loan (POS, FreshStart). Your loan agreement is always the final
            authority on your specific terms. If you have questions not covered here, contact
            us at (888) 502-8082.
          </p>
        </div>

        {/* Loan Type Toggle */}
        <div className="faq-toggle-group" role="group" aria-label="Loan type selection">
          <button
            type="button"
            className={`faq-toggle-btn ${selectedLoanType === FAQ_LOAN_TYPES.REGULAR ? 'is-active' : ''}`}
            aria-pressed={selectedLoanType === FAQ_LOAN_TYPES.REGULAR}
            onClick={() => handleLoanTypeChange(FAQ_LOAN_TYPES.REGULAR)}
          >
            Unsecured Loan (POS, FreshStart)
          </button>
          <button
            type="button"
            className={`faq-toggle-btn ${selectedLoanType === FAQ_LOAN_TYPES.CRYPTO ? 'is-active' : ''}`}
            aria-pressed={selectedLoanType === FAQ_LOAN_TYPES.CRYPTO}
            onClick={() => handleLoanTypeChange(FAQ_LOAN_TYPES.CRYPTO)}
          >
            Crypto Lending Loan
          </button>
        </div>

        {/* Main Heading */}
        <h1 className="faq-main-heading">
          {selectedLoanType === FAQ_LOAN_TYPES.CRYPTO ? 'Crypto Lending Loan FAQs' : 'General Loan FAQs'}
        </h1>
        <hr className="faq-heading-rule" />

        {/* Category FAQ List */}
        {categories.length === 0 ? (
          <p className="faq-empty-state">No FAQs available for this loan type.</p>
        ) : (
          categories.map((category) => (
            <section key={category.id} className="faq-category">
              <h2 className="faq-category__header">{category.title}</h2>
              <div className="faq-category__questions">
                {category.questions.map((item, index) => {
                  const questionKey = getQuestionKey(category.id, index);
                  const isOpen = openQuestionKey === questionKey;
                  return (
                    <div key={questionKey} className={`faq-question ${isOpen ? 'is-open' : ''}`}>
                      <button
                        type="button"
                        className="faq-question__trigger"
                        aria-expanded={isOpen}
                        onClick={() => toggleQuestion(questionKey)}
                      >
                        <span className="faq-question__text">{item.question}</span>
                        <ChevronDown
                          size={18}
                          className={`faq-question__chevron ${isOpen ? 'is-open' : ''}`}
                          aria-hidden="true"
                        />
                      </button>
                      <div className="faq-question__answer-wrap">
                        <p className="faq-question__answer">{item.answer}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))
        )}
      </Container>
    </div>
  );
};

FAQs.propTypes = {
  loansList: PropTypes.shape({
    data: PropTypes.array,
    activeLoanId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

FAQs.defaultProps = {
  loansList: {},
};

const mapStateToProps = (state) => ({
  loansList: state.dashboard.loansList,
});

export default compose(
  connect(mapStateToProps)
)(FAQs);
